import React from 'react';
import { useGetIntegrations } from '@pages/Integrationsv3/hooks';
import { useCommerceFromStore } from '@store/actions/commerce';
import { useElectronPrintSettingsFromStore } from '@store/actions/electronPrintSettings';
import { useElectronPrintersFromStore } from '@store/actions/electronPrinters';
import { ModuleName } from '@utils/constants';
import { useElectron } from '@utils/hooks/useElectron';
import useModules from '@utils/hooks/useModules';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBillingConfig } from '@pages/Commerce/hooks/commerce';
import { useSetupValidation } from '@pages/Setup/hooks/useSetupValidation';
import { useShouldShowSetup } from '@pages/Setup/hooks/useShouldShowSetup';
import {
  IconBrandWhatsappFilled,
  IconMapPinFilled,
  IconTruckDelivery,
  IconCreditCardFilled,
  IconClockFilled,
  IconAlertTriangleFilled,
  IconInfoCircleFilled,
  IconCircleCheckFilled,
} from '@tabler/icons-react';

const ERROR_TYPES_MAP = {
  error: {
    key: 'error',
    icon: <IconAlertTriangleFilled />,
    color: 'bg-color-warning',
  },
  info: {
    key: 'info',
    icon: <IconInfoCircleFilled />,
    color: 'bg-color-info',
  },
  warning: {
    key: 'warning',
    icon: <IconAlertTriangleFilled />,
    color: 'bg-color-warning_yellow',
  },
  success: {
    key: 'success',
    icon: <IconCircleCheckFilled />,
    color: 'bg-color-green',
  },
} as const;

type IWarning = {
  message: string | React.ReactNode;
  onClick: () => void;
  type: keyof typeof ERROR_TYPES_MAP;
  emoji?: string | React.ReactNode; // ✨ Ahora acepta string (emoji) o React component (Tabler icon)
  show: boolean;
  bgColor?: string;
  hideIcon?: boolean;
};

const useStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const commerce = useCommerceFromStore();
  const { hasModule } = useModules();
  const { isElectronInstance } = useElectron();
  const printers = useElectronPrintersFromStore();
  const electronPrinterName = useElectronPrintSettingsFromStore().Printer1.electronPrinterName;
  const integrations = useGetIntegrations();
  const hasPaymentIntegrations = integrations.integrations.some(
    (integration) => integration.provider_type === 'payment',
  );
  const { lastSuscriptionInvoice } = useSubscription();
  const { handleBillingRequest } = useBillingConfig();

  // Setup validation
  const setupStatus = useSetupValidation(commerce);
  const isOnSetupPage = location.pathname === '/configuracion-inicial';
  const shouldShowSetup = useShouldShowSetup();

  const shouldShowSubscriptionWarning = (invoice?: { status?: string; attempt_count?: number }): boolean => {
    return invoice?.status === 'uncollectible' || (invoice?.status === 'open' && (invoice.attempt_count ?? 0) > 0);
  };

  // Setup warnings configuration with priority
  const setupWarningsConfig = [
    {
      config: 'whatsapp' as const,
      priority: 1, // high
      emoji: <IconBrandWhatsappFilled />,
      message: 'Configura tu número de WhatsApp para recibir pedidos y notificaciones.',
      type: ERROR_TYPES_MAP.error.key,
    },
    {
      config: 'address' as const,
      priority: 1, // high
      emoji: <IconMapPinFilled />,
      message: 'Agrega la dirección de tu comercio para que tus clientes puedan encontrarte.',
      type: ERROR_TYPES_MAP.error.key,
    },
    {
      config: 'deliveryMethods' as const,
      priority: 1, // high
      emoji: <IconTruckDelivery />,
      message: 'Selecciona al menos un método de entrega (Domicilio, Recoger, Comer en restaurante).',
      type: ERROR_TYPES_MAP.error.key,
    },
    {
      config: 'paymentMethods' as const,
      priority: 1, // high
      emoji: <IconCreditCardFilled />,
      message: 'Configura los métodos de pago que aceptarás en tu comercio.',
      type: ERROR_TYPES_MAP.error.key,
    },
    {
      config: 'schedules' as const,
      priority: 2, // medium
      emoji: <IconClockFilled />,
      message: 'Define los horarios de atención de tu comercio.',
      type: ERROR_TYPES_MAP.warning.key,
    },
  ];

  // All warnings with explicit priority levels (lower number = higher priority)
  const allWarnings: Array<IWarning & { priority: number }> = [
    // ========================================
    // PRIORIDADES 1-4: CONFIGURACIÓN INICIAL (SETUP)
    // ========================================
    // Estas son las configuraciones esenciales que se manejan en /configuracion-inicial
    // Solo se muestran si NO estamos en esa página

    // PRIORITY 1: WhatsApp (CRÍTICO - sin esto no pueden recibir pedidos)
    {
      priority: 1,
      message: setupWarningsConfig.find((w) => w.config === 'whatsapp')?.message || '',
      onClick: () => {
        if (shouldShowSetup) {
          navigate('/configuracion-inicial');
        } else {
          navigate('/datos-del-comercio');
        }
      },
      type: ERROR_TYPES_MAP.error.key,
      emoji: setupWarningsConfig.find((w) => w.config === 'whatsapp')?.emoji,
      show: !setupStatus.whatsapp.isComplete && !isOnSetupPage,
    },
    // PRIORITY 2: Dirección del negocio (CRÍTICO - necesario para entregas y ubicación)
    {
      priority: 2,
      message: setupWarningsConfig.find((w) => w.config === 'address')?.message || '',
      onClick: () => {
        if (shouldShowSetup) {
          navigate('/configuracion-inicial');
        } else {
          navigate('/datos-del-comercio/ubicacion');
        }
      },
      type: ERROR_TYPES_MAP.error.key,
      emoji: setupWarningsConfig.find((w) => w.config === 'address')?.emoji,
      show: !setupStatus.address.isComplete && !isOnSetupPage,
    },
    // PRIORITY 3: Métodos de entrega (CRÍTICO - sin esto no pueden ordenar)
    {
      priority: 3,
      message: setupWarningsConfig.find((w) => w.config === 'deliveryMethods')?.message || '',
      onClick: () => {
        if (shouldShowSetup) {
          navigate('/configuracion-inicial');
        } else {
          navigate('/tarifas');
        }
      },
      type: ERROR_TYPES_MAP.error.key,
      emoji: setupWarningsConfig.find((w) => w.config === 'deliveryMethods')?.emoji,
      show: !setupStatus.deliveryMethods.isComplete && !isOnSetupPage,
    },
    // PRIORITY 4: Métodos de pago (CRÍTICO - sin esto no pueden pagar)
    {
      priority: 4,
      message: setupWarningsConfig.find((w) => w.config === 'paymentMethods')?.message || '',
      onClick: () => {
        if (shouldShowSetup) {
          navigate('/configuracion-inicial');
        } else {
          navigate('/tarifas');
        }
      },
      type: ERROR_TYPES_MAP.error.key,
      emoji: setupWarningsConfig.find((w) => w.config === 'paymentMethods')?.emoji,
      show: !setupStatus.paymentMethods.isComplete && !isOnSetupPage,
    },
    // PRIORITY 5: Horarios de atención (ALTO - importante pero no bloqueante)
    {
      priority: 5,
      message: setupWarningsConfig.find((w) => w.config === 'schedules')?.message || '',
      onClick: () => {
        if (shouldShowSetup) {
          navigate('/configuracion-inicial');
        } else {
          navigate('/horarios');
        }
      },
      type: ERROR_TYPES_MAP.warning.key,
      emoji: setupWarningsConfig.find((w) => w.config === 'schedules')?.emoji,
      show: !setupStatus.schedules.isComplete && !isOnSetupPage,
    },

    // ========================================
    // PRIORIDADES 6+: ERRORES OPERATIVOS/TÉCNICOS
    // ========================================
    // Estos son errores técnicos que requieren atención pero no son parte del setup inicial

    // PRIORITY 6: Subscription payment issues (CRÍTICO FINANCIERO)
    {
      priority: 6,
      message: (
        <>
          Parece que hubo un problema con el pago de tu suscripción. Da{' '}
          <span className="bg-color-white text-color-primary_dark rounded-lg px-2">CLICK AQUI</span> para intentar
          nuevamente tu pago.
        </>
      ),
      onClick: () => handleBillingRequest(),
      type: ERROR_TYPES_MAP.error.key,
      show: shouldShowSubscriptionWarning(lastSuscriptionInvoice),
    },
    // PRIORITY 7: Payment provider integration missing (ALTO - solo si ya configuraron payment_provider)
    {
      priority: 7,
      message: 'Conecta un proveedor para activar el pago en línea en tu menú.',
      onClick: () => navigate('/integraciones'),
      type: ERROR_TYPES_MAP.error.key,
      show:
        !hasPaymentIntegrations &&
        setupStatus.paymentMethods.isComplete && // Solo mostrar si ya configuraron métodos de pago
        (commerce.metodos_pago_envio_domicilio.payment_provider ||
          commerce.metodos_pago_comer_restaurant.payment_provider ||
          commerce.metodos_pago_pasar_recoger.payment_provider),
    },
    // PRIORITY 8: No shipping method configured (MEDIO-ALTO - solo si ya activaron envío a domicilio)
    {
      priority: 8,
      message:
        'No tienes un método de envío seleccionado, tus comensales no podrán solicitar envíos a domicilio. Da click aquí para configurarlo.',
      onClick: () => navigate('/tarifas'),
      type: ERROR_TYPES_MAP.error.key,
      show: Boolean(
        hasModule(ModuleName.TARIFAS) &&
          setupStatus.deliveryMethods.isComplete && // Solo mostrar si ya configuraron métodos de entrega
          commerce.activar_envio_domicilio &&
          !commerce.tipo_envio_domicilio,
      ),
    },
    // PRIORITY 9: Printer not found (MEDIO)
    {
      priority: 9,
      message: 'No se encontró la impresora principal que tenías seleccionada. Da click aquí para ver las disponibles.',
      onClick: () => navigate('/ajustes'),
      type: ERROR_TYPES_MAP.error.key,
      show:
        isElectronInstance &&
        Boolean(electronPrinterName) &&
        printers.length > 0 &&
        printers.filter((printer) => printer.name === electronPrinterName).length === 0,
    },
    // PRIORITY 10: No main printer configured (MEDIO)
    {
      priority: 10,
      message:
        'No tienes una impresora principal para impresión automática de comandas. Da click aquí para configurarla.',
      onClick: () => navigate('/ajustes'),
      type: ERROR_TYPES_MAP.error.key,
      show: isElectronInstance && !electronPrinterName && printers.length > 0,
    },
    // PRIORITY 11: No printers detected (BAJO)
    {
      priority: 11,
      message: 'No se detectaron impresoras en tu computadora. No podrás imprimir comandas automáticamente.',
      onClick: () => null,
      type: ERROR_TYPES_MAP.error.key,
      show: isElectronInstance && printers.length === 0,
    },
    // PRIORITY 12: Address updated (INFO - MENOR)
    {
      priority: 12,
      message: 'Hemos actualizado la dirección de tu comercio. Da click aquí para revisarla.',
      onClick: () => navigate('/datos-del-comercio/ubicacion'),
      type: ERROR_TYPES_MAP.info.key,
      show: Boolean(commerce.direccion) && setupStatus.address.isComplete, // Solo si ya tienen dirección configurada
    },
  ];

  // Filter visible warnings and get only the highest priority one
  const visibleWarnings = allWarnings.filter((w) => w.show);
  const highestPriorityWarning = visibleWarnings.length > 0 ? visibleWarnings[0] : null;

  return {
    warning: highestPriorityWarning,
  };
};

export const StatusBottomBar = () => {
  const { warning } = useStatus();

  // If no warning to show, return null
  if (!warning) return null;

  return (
    <div className="hover:cursor-pointer overflow-hidden">
      <div
        className={`flex items-center gap-x-2 ${ERROR_TYPES_MAP[warning.type].color} px-4 py-2 text-color-primary_dark text-sm md:text-base overflow-hidden`}
        onClick={warning.onClick}
        style={{ backgroundColor: warning.bgColor || '' }}
      >
        {!warning.hideIcon && warning?.emoji ? (
          // Mostrar emoji personalizado (Tabler icon o string)
          typeof warning.emoji === 'string' ? (
            <span className="text-2xl flex-shrink-0">{warning.emoji}</span>
          ) : (
            <div className="flex items-center justify-center flex-shrink-0">{warning.emoji}</div>
          )
        ) : !warning.hideIcon ? (
          // Mostrar ícono por defecto según el tipo (Tabler icon)
          <div className="flex items-center justify-center flex-shrink-0">{ERROR_TYPES_MAP[warning.type].icon}</div>
        ) : null}
        <span className="font-bold truncate min-w-0">{warning.message}</span>
      </div>
    </div>
  );
};
